import { getApiBase, slDbg } from './config';
import { sealBody } from './crypto';

function jsonInit(payload: unknown, signal?: AbortSignal): RequestInit {
  return {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  };
}

function sealedInit(sealed: Uint8Array<ArrayBuffer>, signal?: AbortSignal): RequestInit {
  return {
    method: 'POST',
    headers: { 'content-type': 'application/octet-stream' },
    body: new Blob([sealed], { type: 'application/octet-stream' }),
    signal,
  };
}

function isParseFailure(status: number, body: string): boolean {
  return status === 400 && /"error"\s*:\s*"(bad json|bad ciphertext)"/.test(body);
}

/**
 * POST a telemetry JSON payload using the sealed binary envelope when available.
 * Some iOS in-app webviews corrupt or strip binary fetch bodies; the worker then
 * rejects the request before validation as `bad json`. Retry once with plaintext
 * JSON, which the worker accepts for rollout/compatibility safety.
 */
export async function postTelemetryJson(
  path: string,
  payload: unknown,
  signal?: AbortSignal,
): Promise<Response> {
  const url = `${getApiBase()}${path}`;
  const sealed = await sealBody(payload);
  if (!sealed) return fetch(url, jsonInit(payload, signal));

  const first = await fetch(url, sealedInit(sealed, signal));
  if (first.ok) return first;

  const firstBody = await first
    .clone()
    .text()
    .catch(() => '');
  if (!isParseFailure(first.status, firstBody)) return first;

  slDbg(`sealed body rejected ${first.status} ${firstBody.slice(0, 120)}; retry json`);
  return fetch(url, jsonInit(payload, signal));
}
